// @ts-strict-ignore
import queryString from 'query-string';

import { SearchHit } from 'api/search';
import { ListingMiniExtra, OrgMini } from 'api/types/orgs-mini.types';
import { FilestackImageData } from 'api/types/shared.types';
import { BackgroundTask, waitTaskCompleted } from 'utils/asyncBackendTask';
import { del, getJson, postJson, putJson } from 'utils/http';

import { OrgSize } from './types/OrgSize';
import { PreferredListingType } from './types/PreferredListingType';

export type Image = {
  filename: string;
  handle: string;
  mimetype: string;
  size: number;
  source: string;
};
export type UserInfo = {
  id: string;
  created: number;
  updated: number;
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  email: string | null | undefined;
  emailVerified: boolean;
  hasPassword: boolean;
  locale: string;
  hasPaymentInfo: boolean;
  locationText: string;
  stateCode: string;
  countryCode: string;
  latitude: number;
  longitude: number;
  timezone: string;
  profile:
    | {
        description: string;
        links: Array<string>;
        image: Image | null | undefined;
      }
    | null
    | undefined;
  isLastOwnerOfOrgs: Array<{
    id: string;
    name: string;
    hasOutstandingInvoices: boolean;
    credits: number;
  }>;
  hasEvents: boolean;
  canExportData: boolean;
};
export type SavedSearch = {
  id: string;
  text: string;
  created: string;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filters: Record<string, any>;
  location: {
    location: string | null | undefined;
    hasGeo: boolean;
    geoType: string | null | undefined;
    stateCode: string | null | undefined;
    countryCode: string | null | undefined;
    latitude: number | null | undefined;
    longitude: number | null | undefined;
    boundingBoxN: number | null | undefined;
    boundingBoxS: number | null | undefined;
    boundingBoxW: number | null | undefined;
    boundingBoxE: number | null | undefined;
  };
  radius: number;
};
export type ListingApplication = {
  created: string;
  id: string;
  listing: ListingMiniExtra;
  org: OrgMini;
};
export type GeneralUserInfo = {
  user: UserInfo;
  savedSearches: Array<SavedSearch>;
  authProviders: Array<string>;
};
export type NotificationPreferences = {
  updatedByUser?: number | null | undefined;
  receiveEmails: boolean | null | undefined;
  receiveEmailsExplicitlyChosen: boolean | null | undefined;
};
export type PaymentMethod = {
  id: string;
  brand: string;
  last4: string;
  name: string;
  type: string;
  onPendingPayment?: boolean;
};
export type SavedListingData = {
  savedListings: Array<SearchHit>;
  page: number;
  count: number;
  pageCount: number;
};
export function getNotificationPreferences(): Promise<NotificationPreferences> {
  return getJson(`/data/userdashboard/notification-preferences`).then(
    ({ notificationPreferences }) => notificationPreferences
  );
}
export function updateNotificationPreferences(
  preferences: NotificationPreferences
): Promise<NotificationPreferences> {
  return putJson(
    `/data/userdashboard/notification-preferences`,
    preferences
  ).then(({ notificationPreferences }) => notificationPreferences);
}
export function getPaymentMethods(): Promise<Array<PaymentMethod>> {
  return getJson('/data/payment-methods').then(({ task }) =>
    waitTaskCompleted(task).then((result) => {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'success' does not exist on type '{}'.
      const { success, sources } = result;

      if (!success) {
        throw new Error(`Error fetching Stripe payment sources`);
      }

      return sources;
    })
  );
}
export type AddPaymentMethodResult = {
  success: boolean;
  source?: PaymentMethod;
  errorHuman?: string;
  error:
    | {
        msg: string;
        stripeCode: string;
      }
    | null
    | undefined;
};
export function addPaymentMethod(
  token: string
): Promise<BackgroundTask<AddPaymentMethodResult>> {
  return postJson(`/data/userdashboard/payment-methods`, {
    token,
  }).then(({ task }) => task);
}
export function removePaymentMethod(methodId: string): Promise<boolean> {
  return del(`/data/userdashboard/payment-methods/${methodId}`).then(
    ({ task }) =>
      waitTaskCompleted(task).then((result) => {
        // @ts-expect-error ts-migrate(2339) FIXME: Property 'success' does not exist on type '{}'.
        const { success } = result;
        return success;
      })
  );
}
export function fetchSavedListings(
  listingType: string,
  page: number
): Promise<SavedListingData> {
  return getJson(
    `/data/userdashboard/saved-listings?listingType=${listingType}&page=${page}`
  );
}
export const fetchListingApplications = (params: {
  page: number | null | undefined;
}) => {
  const qs = queryString.stringify(params);
  return getJson(`/data/listing-applications?${qs}`).then(
    ({ listingApplications }) => listingApplications
  );
};

export type ProfileInfo = {
  description: string;
  links: Array<string>;
  image: FilestackImageData | null | undefined;
};

export type GeneralSettings = {
  firstName: string;
  lastName: string;
  locale: UserLocale;
  email: string;
  location: SearchLocation;
  optedInResearch: boolean;
  profile?: ProfileInfo;
  authProviders: Array<'google_oauth2' | 'facebook' | 'apple'>;
  canExportData: boolean;
  redirectTo?: string;
  orgSize: OrgSize;
  preferredListingTypes: PreferredListingType[];
};

export const fetchGeneralSettings = (): Promise<GeneralSettings> =>
  getJson('/data/userdashboard/general-settings').then(
    ({ generalSettings }) => generalSettings
  );

export const updateSettings = (
  settings: Partial<GeneralSettings>
): Promise<GeneralSettings> =>
  putJson('/data/userdashboard/general-settings', settings).then(
    ({ generalSettings }) => generalSettings
  );

export const updateProfile = (
  profileInfo: ProfileInfo
): Promise<GeneralSettings> =>
  putJson('/data/userdashboard/profile', profileInfo).then(
    ({ generalSettings }) => generalSettings
  );

export const updateMissingInfo = (
  userInfo: Partial<GeneralSettings>
): Promise<GeneralSettings> =>
  postJson('/data/userdashboard/missing-info', userInfo).then(
    ({ generalSettings }) => generalSettings
  );
